/**
 * managementModules.js
 *
 * Single source of truth for the Modules hub (ManagementDataScreen) — the
 * mobile mirror of the admin sidebar taxonomy (admin `src/config/menuStructure.js`
 * / `menuSections`). Tiles are grouped into the same four sections the admin
 * uses (Sales / Main modules / Functional modules / General & system) so users
 * who know the web admin find every module in the same place on mobile.
 *
 * Only metadata lives here (like {@link module:config/managementTabs}); the hub
 * screen resolves labels via i18n and gates every tile through
 * `useManagementAccess().canAccess(key)`.
 *
 * A tile is added ONLY once its target exists as a real native screen or a
 * supported ManagementListScreen `type` — no placeholder/"coming soon" tiles.
 * Admin modules without a native screen yet (rental, HR, marketing,
 * manufacturing, HACCP, fleet, documents hub, statistics,
 * system/dev tools beyond deployments) join this catalog as their screens land.
 */

import {isModuleEnabled} from './featureFlags';

/**
 * @typedef {object} ManagementModuleSection
 * @property {string} id       Section id (mirrors the admin menuSections taxonomy).
 * @property {string} titleKey i18n key of the section heading.
 * @property {string} icon     MaterialCommunityIcons glyph for the section heading.
 * @property {string} tone     ManagementUI tone used as the section accent.
 */

/** @type {ManagementModuleSection[]} */
export const MANAGEMENT_MODULE_SECTIONS = [
    {id: 'sales', titleKey: 'mgmt_modules_section_sales', icon: 'storefront-outline', tone: 'primary'},
    {id: 'main', titleKey: 'mgmt_modules_section_main', icon: 'engine-outline', tone: 'violet'},
    {id: 'functional', titleKey: 'mgmt_modules_section_functional', icon: 'widgets-outline', tone: 'success'},
    {id: 'general', titleKey: 'mgmt_modules_section_general', icon: 'shape-outline', tone: 'info'},
];

/**
 * @typedef {object} ManagementModuleTile
 * @property {string} key        managementAccess rule key — gates the tile via `canAccess(key)`.
 * @property {string} sectionId  Owning {@link ManagementModuleSection} id.
 * @property {string} titleKey   i18n key of the tile title.
 * @property {string} subtitleKey i18n key of the tile subtitle.
 * @property {string} icon       MaterialCommunityIcons glyph name.
 * @property {string} tone       ManagementUI gradient tone (info/success/warning/violet/primary).
 * @property {string} [screen]   Stack route to open (registered in AppNavigator).
 * @property {string} [list]     ManagementListScreen `type` to open.
 */

/**
 * Every tile this codebase knows about, before the build-time switchboard.
 * Use {@link MANAGEMENT_MODULES} to render — this is the unfiltered catalog,
 * kept exported for tooling/tests that need the complete taxonomy.
 * @type {ManagementModuleTile[]}
 */
export const ALL_MANAGEMENT_MODULES = [
    // Sales — day-to-day selling (admin: Értékesítés section).
    {key: 'orders', sectionId: 'sales', titleKey: 'mgmt_data_orders_title', subtitleKey: 'mgmt_data_orders_subtitle', icon: 'clipboard-list-outline', tone: 'info', list: 'orders'},
    {key: 'products', sectionId: 'sales', titleKey: 'mgmt_data_products_title', subtitleKey: 'mgmt_data_products_subtitle', icon: 'food-fork-drink', tone: 'success', list: 'products'},
    {key: 'customers', sectionId: 'sales', titleKey: 'mgmt_data_customers_title', subtitleKey: 'mgmt_data_customers_subtitle', icon: 'account-heart-outline', tone: 'violet', list: 'customers'},

    // Main modules — the big vertical hubs (admin: Fő modulok section).
    {key: 'crm', sectionId: 'main', titleKey: 'mgmt_data_crm_title', subtitleKey: 'mgmt_data_crm_subtitle', icon: 'account-tie-outline', tone: 'violet', screen: 'ManagementCrmScreen'},
    // Apollo.io prospecting — sits beside `crm` because it feeds it: a revealed
    // prospect is imported straight into the CRM as a lead. Its own tile (rather
    // than a CRM sub-page only) because reps open it as a destination of its own.
    {key: 'apollo', sectionId: 'main', titleKey: 'mgmt_data_apollo_title', subtitleKey: 'mgmt_data_apollo_subtitle', icon: 'account-search-outline', tone: 'primary', screen: 'ApolloSearchScreen'},
    {key: 'service', sectionId: 'main', titleKey: 'mgmt_data_service_title', subtitleKey: 'mgmt_data_service_subtitle', icon: 'tools', tone: 'warning', screen: 'ManagementServiceScreen'},
    {key: 'timeTracking', sectionId: 'main', titleKey: 'mgmt_data_time_tracking_title', subtitleKey: 'mgmt_data_time_tracking_subtitle', icon: 'timer-outline', tone: 'violet', screen: 'TimeTrackerScreen'},
    // Projects + customer support (admin: Projektek / Ügyfélszolgálat). Each tile
    // opens the native list; its rows open the read-only detail page.
    {key: 'projects', sectionId: 'main', titleKey: 'mgmt_data_projects_title', subtitleKey: 'mgmt_data_projects_subtitle', icon: 'briefcase-outline', tone: 'violet', list: 'projects'},
    {key: 'tasks', sectionId: 'main', titleKey: 'mgmt_data_tasks_title', subtitleKey: 'mgmt_data_tasks_subtitle', icon: 'checkbox-marked-circle-outline', tone: 'info', list: 'tasks'},
    {key: 'supportTickets', sectionId: 'main', titleKey: 'mgmt_data_support_tickets_title', subtitleKey: 'mgmt_data_support_tickets_subtitle', icon: 'lifebuoy', tone: 'warning', list: 'supportTickets'},
    {key: 'complaints', sectionId: 'main', titleKey: 'mgmt_data_complaints_title', subtitleKey: 'mgmt_data_complaints_subtitle', icon: 'alert-octagon-outline', tone: 'primary', list: 'complaints'},

    // Functional modules — finance / stock / logistics (admin: Funkcionális modulok).
    {key: 'finance', sectionId: 'functional', titleKey: 'mgmt_data_finance_title', subtitleKey: 'mgmt_data_finance_subtitle', icon: 'cash-multiple', tone: 'success', screen: 'ManagementFinanceScreen'},
    {key: 'stock', sectionId: 'functional', titleKey: 'mgmt_data_stock_title', subtitleKey: 'mgmt_data_stock_subtitle', icon: 'warehouse', tone: 'warning', list: 'stock'},
    // Procurement sits beside `stock` and NOT inside it: the admin ships it as
    // its own top-level sidebar group (`beszerzes-csoport`) with its own licence,
    // so a store can have the warehouse module without procurement and vice
    // versa. Its tile opens the module hub, which fans out to requisitions,
    // RFQs, orders, the planner, the reorder rules and the settings.
    {key: 'procurement', sectionId: 'functional', titleKey: 'mgmt_data_procurement_title', subtitleKey: 'mgmt_data_procurement_subtitle', icon: 'cart-outline', tone: 'primary', screen: 'ProcurementHomeScreen'},
    // Utility bills & meters (Rezsi nyilvántartás) — own store module
    // (`rezsi-nyilvantartas`); the hub opens the overview, the field flow (meter
    // reading with photos, bill entry) hangs off its bottom bar.
    {key: 'utilities', sectionId: 'functional', titleKey: 'mgmt_data_utilities_title', subtitleKey: 'mgmt_data_utilities_subtitle', icon: 'flash-outline', tone: 'warning', screen: 'UtilityHomeScreen'},
    // Fleet (Flotta) — role-gated (`fleet.view`, no store-module gate on /api/fleet/*).
    {key: 'fleet', sectionId: 'functional', titleKey: 'mgmt_data_fleet_title', subtitleKey: 'mgmt_data_fleet_subtitle', icon: 'car-outline', tone: 'info', screen: 'ManagementFleetStatsScreen'},
    {key: 'logistics', sectionId: 'functional', titleKey: 'mgmt_data_logistics_title', subtitleKey: 'mgmt_data_logistics_subtitle', icon: 'truck-fast-outline', tone: 'warning', screen: 'ManagementLogisticsScreen'},
    {key: 'integrations', sectionId: 'functional', titleKey: 'mgmt_data_integrations_title', subtitleKey: 'mgmt_data_integrations_subtitle', icon: 'connection', tone: 'info', list: 'integrations'},
    // AI meeting recorder — record a conversation, upload it and read the
    // backend transcript/summary. A workspace feature (like the Time Tracker),
    // not a governable store module, so its access rule carries no module id.
    {key: 'meetings', sectionId: 'functional', titleKey: 'mgmt_data_meetings_title', subtitleKey: 'mgmt_data_meetings_subtitle', icon: 'microphone-outline', tone: 'violet', screen: 'MeetingRecorderScreen'},

    // General & system — settings-grade + admin/system tooling (admin: Általános).
    {key: 'automations', sectionId: 'general', titleKey: 'mgmt_data_automations_title', subtitleKey: 'mgmt_data_automations_subtitle', icon: 'robot-outline', tone: 'violet', screen: 'ManagementAutomationsScreen'},
    {key: 'store', sectionId: 'general', titleKey: 'mgmt_data_store_title', subtitleKey: 'mgmt_data_store_subtitle', icon: 'storefront-outline', tone: 'info', screen: 'ManagementStoreSettingsScreen'},
    {key: 'users', sectionId: 'general', titleKey: 'mgmt_data_users_title', subtitleKey: 'mgmt_data_users_subtitle', icon: 'account-group-outline', tone: 'violet', screen: 'ManagementUsersScreen'},
    {key: 'roles', sectionId: 'general', titleKey: 'mgmt_data_roles_title', subtitleKey: 'mgmt_data_roles_subtitle', icon: 'shield-account-outline', tone: 'info', screen: 'ManagementRolesScreen'},
    {key: 'moduleTemplates', sectionId: 'general', titleKey: 'mgmt_data_module_templates_title', subtitleKey: 'mgmt_data_module_templates_subtitle', icon: 'widgets-outline', tone: 'violet', screen: 'ManagementModuleTemplatesScreen'},
    {key: 'billing', sectionId: 'general', titleKey: 'mgmt_data_billing_title', subtitleKey: 'mgmt_data_billing_subtitle', icon: 'credit-card-outline', tone: 'success', screen: 'ManagementBillingScreen'},
    {key: 'deployments', sectionId: 'general', titleKey: 'mgmt_data_deployments_title', subtitleKey: 'mgmt_data_deployments_subtitle', icon: 'rocket-launch-outline', tone: 'warning', screen: 'ManagementDeploymentsScreen'},
    {key: 'systemStatus', sectionId: 'general', titleKey: 'mgmt_data_system_status_title', subtitleKey: 'mgmt_data_system_status_subtitle', icon: 'pulse', tone: 'info', screen: 'ManagementSystemStatusScreen'},
];

/**
 * The tiles this BUILD ships, after the release profile in
 * {@link module:config/featureFlags} has had its say.
 *
 * The distinction matters: a tile the user's role or license does not cover
 * still renders (locked, with a reason) — that is intentional upsell UX. A tile
 * this release was built without must not render at all, because an App Store
 * reviewer seeing a locked tile for an unfinished module is exactly what got the
 * submission rejected.
 * @type {ManagementModuleTile[]}
 */
export const MANAGEMENT_MODULES = ALL_MANAGEMENT_MODULES.filter((tile) => isModuleEnabled(tile.key));

/**
 * Group the visible module tiles by section, preserving the section order.
 * Sections with no visible tile are dropped so the hub never renders an empty
 * heading (e.g. every tile filtered out by search or access).
 *
 * @param {ManagementModuleTile[]} tiles The (already filtered) tiles to group.
 * @returns {Array<{section: ManagementModuleSection, tiles: ManagementModuleTile[]}>}
 */
export const groupModulesBySection = (tiles) => MANAGEMENT_MODULE_SECTIONS
    .map((section) => ({
        section,
        tiles: (Array.isArray(tiles) ? tiles : []).filter((tile) => tile.sectionId === section.id),
    }))
    .filter((group) => group.tiles.length > 0);

export default MANAGEMENT_MODULES;
